import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { horsesAPI } from '../services/api';
import { formatUtcDate } from '../utils/dateFormat';
import { actionsKabobCellSx, actionsKabobHeaderSx } from '../theme/tableStyles';
import ConfirmDialog from '../components/ConfirmDialog';
import RowActionsMenu from '../components/RowActionsMenu';
import {
  tablePageRootSx,
  tablePageHeaderStickySx,
  tablePageScrollRegionSx,
  tablePageTableContainerSx,
} from '../theme/tablePageLayout';
import { toArray } from '../utils/apiData';
import { createPrefixFilter } from '../utils/autocompleteFilters';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import useFieldErrors from '../utils/useFieldErrors';
import { TableSkeletonRows } from '../components/LoadingSkeletons';

const getHorseLabel = (horse) =>
  horse.registered_name?.trim() || horse.name?.trim() || `Horse #${horse.id}`;

const sortHorsesByRegisteredName = (list) =>
  [...list].sort((a, b) => {
    const an = (a.registered_name || a.name || '').toLowerCase();
    const bn = (b.registered_name || b.name || '').toLowerCase();
    return an.localeCompare(bn);
  });

const AdminHorsePedigreesPage = () => {
  const notify = useNotify();
  const fields = useFieldErrors();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pedigrees, setPedigrees] = useState([]);
  const [horses, setHorses] = useState([]);
  const [error, setError] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPedigree, setSelectedPedigree] = useState(null);
  const [formData, setFormData] = useState({
    horse_id: '',
    pedigree: '',
  });

  const loadData = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      // Load all horses for the dropdown
      const horsesRes = await horsesAPI.getAll({});
      const horseRows = toArray(horsesRes.data, 'horses');
      setHorses(horseRows);

      // Load all pedigrees - we'll need to get them individually
      // Since there's no bulk endpoint, we'll get horses and check which have pedigrees
      const horsesWithPedigrees = [];
      for (const horse of horseRows) {
        try {
          const pedigreeRes = await horsesAPI.getPedigree(horse.id);
          if (pedigreeRes.data) {
            horsesWithPedigrees.push({
              ...pedigreeRes.data,
              horse_name: getHorseLabel(horse),
            });
          }
        } catch (err) {
          // Horse doesn't have a pedigree, skip
          if (err.response?.status !== 404) {
            console.error(`Error loading pedigree for horse ${horse.id}:`, err);
          }
        }
      }
      setPedigrees(horsesWithPedigrees);
    } catch (err) {
      console.error('Error loading pedigrees:', err);
      setError(err.response?.data?.error || 'Failed to load pedigrees');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenEdit = (pedigree) => {
    fields.reset();
    setSelectedPedigree(pedigree);
    setFormData({
      horse_id: String(pedigree.horse_id),
      pedigree: pedigree.pedigree || '',
    });
    setEditDialogOpen(true);
  };

  const handleOpenAdd = () => {
    fields.reset();
    setSelectedPedigree(null);
    setFormData({
      horse_id: '',
      pedigree: '',
    });
    setEditDialogOpen(true);
  };

  const handleCloseEdit = () => {
    setSelectedPedigree(null);
    setFormData({
      horse_id: '',
      pedigree: '',
    });
    setEditDialogOpen(false);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    fields.clear(name);
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const valid = fields.validate({
      horse_id: !formData.horse_id && 'Horse is required',
      pedigree: !formData.pedigree.trim() && 'Pedigree HTML is required',
    });
    if (!valid) return;

    setSaving(true);
    try {
      if (selectedPedigree) {
        // Update existing
        await horsesAPI.updatePedigree(selectedPedigree.horse_id, formData.pedigree);
      } else {
        // Create new
        await horsesAPI.savePedigree(Number(formData.horse_id), formData.pedigree);
      }
      handleCloseEdit();
      loadData();
    } catch (err) {
      console.error('Error saving pedigree:', err);
      notify.error(errorMessage(err, 'Failed to save pedigree'));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDelete = (pedigree) => {
    setSelectedPedigree(pedigree);
    setDeleteDialogOpen(true);
  };

  const handleCloseDelete = () => {
    setSelectedPedigree(null);
    setDeleteDialogOpen(false);
  };

  const handleDelete = async () => {
    if (!selectedPedigree) return;

    try {
      await horsesAPI.deletePedigree(selectedPedigree.horse_id);
      handleCloseDelete();
      loadData();
    } catch (err) {
      console.error('Error deleting pedigree:', err);
      notify.error(errorMessage(err, 'Failed to delete pedigree'));
    }
  };

  const getHorseName = (horseId) => {
    const horse = horses.find((h) => h.id === horseId);
    return horse ? getHorseLabel(horse) : `Horse #${horseId}`;
  };

  const horsesForSelect = useMemo(
    () => sortHorsesByRegisteredName(horses),
    [horses],
  );

  const displayDate = (dateString) => formatUtcDate(dateString) ?? 'N/A';

  return (
    <Box sx={tablePageRootSx}>
      <Box sx={tablePageHeaderStickySx}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: { xs: 'flex-start', sm: 'space-between' },
            alignItems: 'center',
            gap: { xs: 1.5, sm: 0 },
            mb: 3,
          }}
        >
          <Typography variant="h4">Horse Pedigrees</Typography>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={handleOpenAdd}
            sx={{
              minWidth: { xs: 0, sm: 'auto' },
              width: { xs: 32, sm: 'auto' },
              height: { xs: 32, sm: 'auto' },
              px: { xs: 0, sm: 2 },
              '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Add Pedigree
            </Box>
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </Box>

      <Box sx={tablePageScrollRegionSx}>
      <TableContainer component={Paper} sx={tablePageTableContainerSx}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={actionsKabobHeaderSx}>Actions</TableCell>
              <TableCell>Horse</TableCell>
              <TableCell>Pedigree Preview</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && <TableSkeletonRows columns={5} />}
            {pedigrees.map((pedigree) => (
              <TableRow key={pedigree.id}>
                <TableCell align="center" sx={actionsKabobCellSx}>
                  <RowActionsMenu
                    actions={[
                      { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => handleOpenEdit(pedigree) },
                      { key: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: () => handleOpenDelete(pedigree), color: 'error' },
                    ]}
                  />
                </TableCell>
                <TableCell>{pedigree.horse_name || getHorseName(pedigree.horse_id)}</TableCell>
                <TableCell>
                  <Box
                    sx={{
                      maxWidth: 300,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={pedigree.pedigree?.substring(0, 200)}
                  >
                    {pedigree.pedigree?.substring(0, 100) || 'No pedigree data'}...
                  </Box>
                </TableCell>
                <TableCell>{displayDate(pedigree.created_at)}</TableCell>
                <TableCell>{displayDate(pedigree.updated_at)}</TableCell>
              </TableRow>
            ))}
            {!loading && pedigrees.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No pedigrees found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>

      {/* Edit/Add Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEdit}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle>
          {selectedPedigree ? 'Edit Pedigree' : 'Add Pedigree'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Autocomplete
              size="small"
              fullWidth
              disableClearable
              disabled={!!selectedPedigree}
              options={horsesForSelect}
              getOptionLabel={getHorseLabel}
              isOptionEqualToValue={(o, v) => String(o.id) === String(v?.id)}
              filterOptions={createPrefixFilter(getHorseLabel)}
              value={horsesForSelect.find((horse) => String(horse.id) === formData.horse_id) ?? null}
              onChange={(e, newValue) => {
                fields.clear('horse_id');
                setFormData((prev) => ({ ...prev, horse_id: newValue ? String(newValue.id) : '' }));
              }}
              renderInput={(params) => <TextField {...params} label="Horse" required {...fields.props('horse_id')} />}
            />
            <TextField
              size="small"
              name="pedigree"
              label="Pedigree HTML"
              value={formData.pedigree}
              onChange={handleFormChange}
              {...fields.props('pedigree', 'Paste the HTML pedigree data here')}
              required
              fullWidth
              multiline
              rows={15}
              sx={{
                '& .MuiInputBase-input': {
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Pedigree"
        message={`Permanently delete the pedigree for ${
          selectedPedigree
            ? selectedPedigree.horse_name || getHorseName(selectedPedigree.horse_id)
            : ''
        }? This cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={handleCloseDelete}
      />
    </Box>
  );
};

export default AdminHorsePedigreesPage;
